/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : AiSuggestionContext.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Typography,
  LinearProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import PropTypes from "prop-types";
import CustomDataGridV2 from "../common/datagrid/CustomDataGridV2";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import { useEffect, useState } from "react";
import helper from "../../../utils/helper";
import { useDispatch } from "react-redux";
import { HandleApiActions } from "../../../redux/actions";
import smartMatchActions from "../../../redux/actions/smart-matching/smartMatchSuggestionActions";
import { FeedbackAction } from "./constants/feedbackTypes";
import CustomToolTip from "../../infoButton/CustomToolTip";

const suggestionStrengthColorMap = {
  high: "#26890D",
  medium: "#0076A8",
  low: "#111111",
};

const LOADING_MESSAGE_MAX_LENGTH = 50;

const formatLoadingMessage = (text, maxLength = LOADING_MESSAGE_MAX_LENGTH) => {
  const message = String(text || "")
    .replace(/^\[VALIDATION\]\s*/i, "")
    .replace(/^\[[A-Z0-9_-]+\]\s*/, "");
  return message.length > maxLength
    ? `${message.slice(0, maxLength).trimEnd()}...`
    : message;
};

const getUnlinkedTestCaseTooltip = (testCase) => {
  const missingLinks = [];

  if (testCase?.is_component_linked === false) {
    missingLinks.push("component");
  }
  if (testCase?.is_lr_linked === false) {
    missingLinks.push(
      missingLinks.length === 0 &&
        testCase?.is_component_linked !== false &&
        testCase?.is_campaign_linked !== false
        ? "legal requirements"
        : "legal requirement",
    );
  }
  if (testCase?.is_campaign_linked === false) {
    missingLinks.push("campaign");
  }

  return missingLinks.length
    ? `Test case not linked to any ${missingLinks.join(", ")}`
    : "";
};

export const isVehicleSystemRecommendation = (row) => {
  if (!row) return false;
  const targetContext = row?.raw?.targetContext || row?.targetContext;
  const target = row?.raw?.target || row?.target;
  const type =
    targetContext?.entityType ||
    target?.entityType ||
    row?.raw?.entityType ||
    row?.raw?.type ||
    row?.entityType ||
    row?.type ||
    (typeof row?.id === "string" && row.id.startsWith("VS_") ? "VS" : "");

  const normalized = String(type || "").trim().toUpperCase();
  return (
    normalized === "VS" ||
    normalized === "VEHICLE_SYSTEM" ||
    normalized === "VEHICLE_SYSTEMS" ||
    normalized === "VEHICLESYSTEM"
  );
};

export const getVsId = (row) => {
  if (!row) return undefined;
  const targetContext = row?.raw?.targetContext || row?.targetContext;
  const target = row?.raw?.target || row?.target;
  const vs_id =
    targetContext?.vs_id ??
    targetContext?.entityId ??
    target?.vs_id ??
    target?.entityId ??
    row?.raw?.vs_id ??
    row?.raw?.entityId ??
    row?.vs_id ??
    row?.entityId ??
    (row?.type === "VS" ? row?.id : undefined) ??
    (typeof row?.id === "string" && row.id.startsWith("VS_")
      ? row.id.replace(/^VS_/, "")
      : undefined);

  return vs_id !== undefined ? vs_id : row?.id;
};

export const isRowMatch = (r1, r2) => {
  if (!r1 || !r2) return false;
  if (r1.id && r2.id && r1.id === r2.id) return true;
  const id1 = r1.raw?.targetContext?.entityId ?? r1.raw?.target?.entityId;
  const id2 = r2.raw?.targetContext?.entityId ?? r2.raw?.target?.entityId;
  const type1 = r1.raw?.targetContext?.entityType ?? r1.raw?.target?.entityType;
  const type2 = r2.raw?.targetContext?.entityType ?? r2.raw?.target?.entityType;
  return Boolean(
    id1 !== undefined &&
      id1 !== null &&
      id2 !== undefined &&
      id2 !== null &&
      id1 === id2 &&
      type1 === type2,
  );
};

export const isRowSelectedInTicket = (
  row,
  { selectedComponents = [], selectedLegals = [], selectedTestCases = [] } = {},
) => {
  if (!row) return false;
  const target = row?.raw?.targetContext || row?.raw?.target || {};
  const type = target.entityType || row?.type;
  const entityId = target.entityId ?? row?.id;
  const number = target.number ?? row?.component?.number;

  if (type === "TEST_CASE") {
    return (selectedTestCases || []).some((tc) => {
      const tcId = tc?.test_case_id ?? tc?.id;
      const origId = tc?.original_test_case_id;
      const tcNumber = tc?.manual_test_case_id ?? tc?.req_number;
      return (
        (entityId !== undefined &&
          entityId !== null &&
          (String(tcId) === String(entityId) ||
            (origId && String(origId) === String(entityId)))) ||
        (number && tcNumber && String(tcNumber) === String(number))
      );
    });
  }

  if (type === "LEGAL_REQUIREMENT") {
    return (selectedLegals || []).some((lr) => {
      const lrId = lr?.lr_id ?? lr?.id;
      const lrNumber = lr?.req_number ?? lr?.number;
      return (
        (entityId !== undefined &&
          entityId !== null &&
          String(lrId) === String(entityId)) ||
        (number && lrNumber && String(lrNumber) === String(number))
      );
    });
  }

  if (type === "EE" || type === "SWM" || type === "VC" || type === "VS") {
    return (selectedComponents || []).some((comp) => {
      const compId =
        comp?.ee_id ?? comp?.swm_id ?? comp?.vc_id ?? comp?.vs_id ?? comp?.id;
      const compNumber =
        comp?.assembly_number ?? comp?.system_number ?? comp?.number;
      const isDirectMatch =
        (entityId !== undefined &&
          entityId !== null &&
          String(compId) === String(entityId)) ||
        (number && compNumber && String(compNumber) === String(number));
      if (isDirectMatch) return true;

      if (type === "VC" && Array.isArray(comp?.vcData)) {
        return comp.vcData.some((vc) => {
          const vcId = vc?.vc_id ?? vc?.id;
          const vcNumber = vc?.component_number ?? vc?.number;
          return (
            (entityId !== undefined &&
              entityId !== null &&
              String(vcId) === String(entityId)) ||
            (number && vcNumber && String(vcNumber) === String(number))
          );
        });
      }
      return false;
    });
  }

  return false;
};

export const mapRecommendationsToRows = (recommendations, source) =>
  recommendations?.map((rec, index) => {
    const entityType =
      rec?.targetContext?.entityType || rec?.target?.entityType || "ENTITY";
    const entityId = rec?.targetContext?.entityId ?? rec?.target?.entityId;
    const rowId =
      entityId !== undefined && entityId !== null
        ? `${entityType}_${entityId}`
        : `rec_${index + 1}`;

    return {
      id: rowId,

      sourceNodeId: source?.nodeId,

    component: {
      number: rec?.targetContext?.number,
      description: rec?.targetContext?.description,
      version: rec?.targetContext?.version,
      zone: rec?.targetContext?.zone,
      interface:
        rec?.targetContext?.networkInterfaces
          ?.map((ni) => ni.name)
          .join(", ") || "N/A",
    },

    parentContext: {
      entityName: rec?.targetContext?.parent?.description || "N/A",
      entityType: rec?.targetContext?.parent?.entityType || "N/A",
      standardNumber:
        rec?.targetContext?.parentContext?.standardNumber || "N/A",
    },

    score: {
      value: rec.suggestionStrength,
      summary: rec.explanation.summary,
      recommendation: rec,
    },

    combinations: rec?.combinations || rec?.combination || [],

    raw: rec, // keep full object if needed later

    isLinked: false, // UI state
    isLinking: false,

    feedback: null, // APPROVED | REJECTED | null
    isProvidingFeedback: false,

    contextualApplicability: rec.contextualApplicability !== false,
    };
  });

export const mapCombinationsToRows = (combinations, parentRow, source) => {
  if (!Array.isArray(combinations)) return [];
  const parentRec = parentRow?.raw || {};
  const parentContext = parentRec?.targetContext?.parentContext || {};
  const parentObj = parentRec?.targetContext?.parent || {};

  return combinations.map((comb, index) => {
    const entityType =
      comb?.entityType || parentRec?.targetContext?.entityType || "LEGAL_REQUIREMENT";
    const entityId = comb?.entityId ?? index + 1;

    const rawComb = {
      ...parentRec,
      target: {
        entityType: comb?.entityType || parentRec?.target?.entityType || entityType,
        entityId: entityId,
      },
      targetContext: {
        ...parentRec?.targetContext,
        ...comb,
        entityId: entityId,
        entityType: entityType,
        number: comb?.number || parentRec?.targetContext?.number || "N/A",
        description: comb?.description || parentRec?.targetContext?.description || "N/A",
        version: comb?.version || parentRec?.targetContext?.version || "1",
        zone: comb?.zone || parentRec?.targetContext?.zone || null,
        networkInterfaces:
          comb?.networkInterfaces || parentRec?.targetContext?.networkInterfaces || [],
        parentContext: comb?.parentContext || parentContext,
        parent: comb?.parent || parentObj,
      },
      suggestionStrength:
        comb?.suggestionStrength || parentRec?.suggestionStrength || "High",
      explanation: comb?.explanation || {
        summary:
          comb?.description ||
          parentRec?.explanation?.summary ||
          "Combination Legal Requirement",
        tracePaths: parentRec?.explanation?.tracePaths || [],
      },
      combinations: comb?.combinations || [],
    };

    return {
      id: `comb-${entityId}-${index}`,
      sourceNodeId: source?.nodeId || parentRow?.sourceNodeId,

      component: {
        number: rawComb.targetContext.number,
        description: rawComb.targetContext.description,
        version: rawComb.targetContext.version,
        zone: rawComb.targetContext.zone,
        interface:
          rawComb.targetContext.networkInterfaces?.map((ni) => ni.name).join(", ") ||
          "N/A",
      },

      parentContext: {
        entityName:
          rawComb.targetContext.parent?.description ||
          parentObj?.description ||
          parentContext?.name ||
          "N/A",
        entityType:
          rawComb.targetContext.parent?.entityType ||
          parentObj?.entityType ||
          "N/A",
        standardNumber:
          rawComb.targetContext.parentContext?.standardNumber ||
          parentContext?.standardNumber ||
          "N/A",
      },

      score: {
        value: rawComb.suggestionStrength,
        summary: rawComb.explanation.summary,
        recommendation: rawComb,
      },

      combinations: rawComb.combinations,
      raw: rawComb,

      isLinked: false,
      isLinking: false,
      feedback: null,
      isProvidingFeedback: false,
      contextualApplicability: true,
      isCombinationRow: true,
    };
  });
};

export const adaptRowToSelectionPayload = (row) => {
  if (!row) return null;
  const raw = row?.raw || row || {};
  const target =
    raw?.targetContext || raw?.target || row?.targetContext || row?.target || {};
  const rawEntityType =
    target?.entityType ||
    raw?.target?.entityType ||
    raw?.entityType ||
    raw?.type ||
    row?.type ||
    "";
  const entityType = String(rawEntityType).toUpperCase();
  const rawId =
    target?.entityId ??
    target?.id ??
    raw?.target?.entityId ??
    raw?.entityId ??
    raw?.id ??
    raw?.vc_id ??
    row?.id;

  const parsedId =
    typeof rawId === "string" && rawId.includes("_")
      ? rawId.split("_").pop()
      : rawId;
  const entityId =
    parsedId !== null && parsedId !== undefined && !isNaN(Number(parsedId))
      ? Number(parsedId)
      : rawId;

  const parentContext =
    target?.parentContext ||
    raw?.targetContext?.parentContext ||
    raw?.parentContext;
  const parent =
    target?.parent ||
    raw?.targetContext?.parent ||
    raw?.parent;

  if (entityType === "EE") {
    return {
      type: "EE",
      ee_id: entityId,
      id: entityId,
      assembly_number: target.number || raw?.assembly_number || row?.component?.number,
      description: target.description || raw?.description || row?.component?.description,
      version: target.version || raw?.version || row?.component?.version,
      linked_ee_ids: [],
      parentContext,
      parent,
      _ai: raw,
    };
  }

  if (entityType === "SWM") {
    return {
      type: "SWM",
      swm_id: entityId,
      id: entityId,
      name: target.number || raw?.name || row?.component?.number,
      description: target.description || raw?.description || row?.component?.description,
      version: target.version || raw?.version || row?.component?.version,
      linked_swm_ids: [],
      ee_id: parentContext?.parent_id || parentContext?.id || raw?.ee_id,
      parentContext,
      parent,
      _ai: raw,
    };
  }

  if (
    entityType === "VC" ||
    entityType === "VEHICLE_COMPONENT" ||
    entityType === "VEHICLE-COMPONENT"
  ) {
    return {
      type: "VC",
      vc_id: entityId,
      id: entityId,
      name:
        target?.description ||
        target?.name ||
        row?.component?.description ||
        raw?.name ||
        "",
      component_number:
        target?.number ||
        target?.component_number ||
        row?.component?.number ||
        raw?.component_number ||
        "",
      description:
        target?.description ||
        row?.component?.description ||
        raw?.description ||
        "",
      pr_vs_id:
        parentContext?.parent_vs_id ||
        parentContext?.parent_id ||
        parentContext?.id ||
        parent?.entityId ||
        parent?.id ||
        raw?.pr_vs_id,
      parentContext,
      parent,
      _ai: raw,
    };
  }

  if (
    entityType === "VS" ||
    entityType === "VEHICLE_SYSTEM" ||
    entityType === "VEHICLE-SYSTEM"
  ) {
    return {
      type: "VS",
      vs_id: entityId,
      id: entityId,
      name:
        target?.description ||
        target?.name ||
        row?.component?.description ||
        raw?.name ||
        "",
      system_number:
        target?.number ||
        target?.system_number ||
        row?.component?.number ||
        raw?.system_number ||
        "",
      description:
        target?.description ||
        row?.component?.description ||
        raw?.description ||
        "",
      version: target.version || raw?.version || row?.component?.version,
      _ai: raw,
    };
  }

  if (entityType === "LEGAL_REQUIREMENT" || entityType === "LEGAL") {
    return {
      type: "LEGAL_REQUIREMENT",
      lr_id: entityId,
      id: entityId,
      req_number: target.number || raw?.req_number || row?.component?.number,
      description: target.description || raw?.description || row?.component?.description,
      version: target.version || raw?.version || row?.component?.version,
      parentContext,
      _ai: raw,
    };
  }

  if (entityType === "TEST_CASE") {
    return {
      type: "TEST_CASE",
      tc_id: entityId,
      test_case_id: entityId,
      id: entityId,
      manual_test_case_id: target?.number || raw?.manual_test_case_id,
      req_number: target.number || raw?.req_number,
      description: target.description || raw?.description,
      version: target.version || raw?.version,
      is_component_linked:
        target?.is_component_linked ??
        raw?.target?.is_component_linked ??
        raw?.is_component_linked ??
        row?.is_component_linked,
      is_lr_linked:
        target?.is_lr_linked ??
        raw?.target?.is_lr_linked ??
        raw?.is_lr_linked ??
        row?.is_lr_linked,
      is_campaign_linked:
        target?.is_campaign_linked ??
        raw?.target?.is_campaign_linked ??
        raw?.is_campaign_linked ??
        row?.is_campaign_linked,
      parentContext,
      _ai: raw,
    };
  }

  return null;
};

export const adaptRowToEEItem = (row) => {
  const target = row?.raw?.targetContext || {};
  const payload = adaptRowToSelectionPayload(row) || {};

  return {
    ...payload,
    type: target.entityType || payload.type,
    ee_id: target.entityId,
    swm_id: target.entityId,
    vc_id: target.entityId,
    vs_id: target.entityId,
    id: target.entityId,
    lr_id: target.entityId,
    tc_id: target.entityId,
    test_case_id: target.entityId,
    manual_test_case_id: target.number,
    assembly_number: target.number,
    description: target.description,
    version: target.version,
    linked_ee_ids: [],
    raw: row.raw,
  };
};

const AiSuggestionRowActionsTooltips = [
  "Approve Suggestion",
  "Reject Suggestion",
  "Link to this component",
  "Unlink from this component",
];

export const getColumns = ({
  mode = "regular",
  onWhyThis,
  handleLink,
  handleUnlink,
  submitFeedback,
  onOpenCombination,
  onOpenAlternate,
  onAddVc,
  rows,
  hasVehicleSystem: hasVehicleSystemProp,
}) => {
  const hasVehicleSystem =
    mode !== "preview" &&
    (hasVehicleSystemProp !== undefined
      ? hasVehicleSystemProp
      : rows
      ? rows.some((row) => isVehicleSystemRecommendation(row))
      : true);

  return [
  {
    field: "component",
    headerName: "Component",
    minWidth: hasVehicleSystem ? 240 : 320,
    sortable: false,
    renderCell: ({ value, row }) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "start",
          height: "100%",
          width: "100%",
          flexDirection: "column",
        }}
      >
        {row?.parentContext?.standardNumber &&
          row.parentContext.standardNumber !== "N/A" && (
            <Typography variant="body2" color="text.primary">
              <strong>
                {helper.toSentenceCase("Standard:")}{" "}
                {row.parentContext.standardNumber}
              </strong>
            </Typography>
          )}

        <Typography
          variant="body2"
          color="text.primary"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <strong>
            {helper.toSentenceCase("Number:")} {value.number}
          </strong>
          {row?.raw?.targetContext?.entityType === "TEST_CASE" &&
            getUnlinkedTestCaseTooltip(row.raw.targetContext) && (
              <CustomToolTip
                type="component"
                title={getUnlinkedTestCaseTooltip(row.raw.targetContext)}
              >
                <ErrorOutlineIcon sx={{ color: "black", fontSize: 18 }} />
              </CustomToolTip>
            )}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {helper.toSentenceCase("Version:")} {value.version}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {helper.toSentenceCase("Description:")} {value.description}
        </Typography>
        {value.zone && (
          <Typography variant="body2" color="text.secondary">
            {helper.toSentenceCase("Zone Details:")} {value.zone}
          </Typography>
        )}
      </Box>
    ),
  },

  {
    field: "parentContext",
    headerName: "EE Context",
    minWidth: 100,
    sortable: false,
    renderCell: ({ value }) =>
      value?.entityName === "N/A" ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
            flexDirection: "column",
          }}
        ></Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            position: "relative",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <Typography variant="body2">
            <strong>EE:{value.entityName}</strong>
          </Typography>
        </Box>
      ),
  },
  ...(hasVehicleSystem
    ? [
        {
          field: "addVc",
          headerName: "",
          width: 90,
          minWidth: 90,
          sortable: false,
          align: "center",
          renderCell: ({ row }) => {
            if (!isVehicleSystemRecommendation(row)) {
              return null;
            }

            const isAdded = Boolean(row.isLinked);
            const vs_id = getVsId(row);

            return (
              <Box sx={{ height: "100%" }}>
                <Tooltip
                  title={
                    isAdded
                      ? "Add Vehicle Component"
                      : "Add Vehicle System first"
                  }
                >
                  <Box
                    role="button"
                    tabIndex={isAdded ? 0 : -1}
                    aria-disabled={!isAdded}
                    onClick={(e) => {
                      if (!isAdded) return;
                      if (e?.stopPropagation) e.stopPropagation();
                      console.log(vs_id);
                      if (onAddVc) {
                        onAddVc(vs_id, row);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (!isAdded) return;
                      if (e.key === "Enter" || e.key === " ") {
                        if (e?.stopPropagation) e.stopPropagation();
                        console.log(vs_id);
                        if (onAddVc) {
                          onAddVc(vs_id, row);
                        }
                      }
                    }}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      width: "100%",
                      flexDirection: "column",
                      cursor: isAdded ? "pointer" : "not-allowed",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        position: "absolute",
                        fontWeight: 600,
                        lineHeight: 1,
                        color: isAdded
                          ? suggestionStrengthColorMap.high || "#26890D"
                          : "rgba(0, 0, 0, 0.26)",
                        ...(isAdded
                          ? {
                              "&:hover": {
                                color: "#20730B",
                              },
                            }
                          : {}),
                      }}
                    >
                      Add VC
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>
            );
          },
        },
      ]
    : []),
  {
    field: "combinations",
    headerName: "",
    width: 10,
    minWidth: 10,
    sortable: false,
    align: "center",
    renderCell: ({ row }) => {
      if (mode === "combination") {
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Tooltip title="Alternate Suggestions">
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (onOpenAlternate) {
                      onOpenAlternate(row);
                    }
                  }}
                  sx={{
                    color: "#0076A8",
                    ":hover": { color: "#26890D" },
                  }}
                >
                  <CallSplitIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      }

      if (mode === "alternate" || mode === "preview") {
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          />
        );
      }

      // Regular mode
      const combinations =
        row?.combinations ||
        row?.raw?.combinations ||
        row?.raw?.combination ||
        [];
      const isEnabled =
        Array.isArray(combinations) && combinations.length > 1;

      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Tooltip title={isEnabled ? "View Combinations" : ""}>
            <span>
              <IconButton
                size="small"
                disabled={!isEnabled}
                onClick={() => {
                  if (isEnabled && onOpenCombination) {
                    onOpenCombination(combinations, row);
                  }
                }}
                sx={{
                  color: isEnabled ? "#000" : "rgba(0, 0, 0, 0.26)",
                  ":hover": isEnabled ? { color: "#26890D" } : {},
                }}
              >
                <AltRouteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      );
    },
  },
  {
    field: "score",
    headerName: "Score",
    width: 90,
    minWidth: 90,
    sortable: false,
    align: "center",
    renderCell: ({ value, row }) => {
      const recommendation = row.raw;

      return (
        <Box sx={{ height: "100%" }}>
          <Box
            onClick={() => {
              onWhyThis(recommendation);
            }}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              width: "100%",
              flexDirection: "column",
              cursor: "pointer",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                fontWeight: 600,
                lineHeight: 1,
                color:
                  suggestionStrengthColorMap[
                    String(value.value || "").toLowerCase()
                  ] || "#111111",
              }}
            >
              {value.value}
            </Typography>
          </Box>
        </Box>
      );
    },
  },

  {
    field: "raw",
    headerName: "",
    minWidth: 180,
    width: "fit-content",
    sortable: false,
    align: "right",
    renderCell: ({ row }) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          position: "relative",
          width: "100%",
        }}
      >
        <Tooltip title={AiSuggestionRowActionsTooltips[0]}>
          <IconButton
            size="small"
            disabled={
              row.isFeedbacking || row.feedback === FeedbackAction.APPROVED
            }
            onClick={() => submitFeedback(row, FeedbackAction.APPROVED)}
            sx={{
              color:
                row.feedback === FeedbackAction.APPROVED ? "#26890D" : "#000",
              ":hover": { color: "#26890D" },
            }}
          >
            <ThumbUpIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={AiSuggestionRowActionsTooltips[1]}>
          <IconButton
            size="small"
            disabled={
              row.isFeedbacking || row.feedback === FeedbackAction.REJECTED
            }
            onClick={() => submitFeedback(row, FeedbackAction.REJECTED)}
            sx={{
              color:
                row.feedback === FeedbackAction.REJECTED ? "#26890D" : "#000",
              ":hover": { color: "#26890D" },
            }}
          >
            <ThumbDownIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {row.isLinked || mode === "preview" ? (
          <Tooltip title={AiSuggestionRowActionsTooltips[3]}>
            <IconButton
              size="small"
              disabled={row.isLinking}
              onClick={() => handleUnlink(row.id)}
              sx={{ color: "#000", ":hover": { color: "#26890D" } }}
            >
              <RemoveCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title={AiSuggestionRowActionsTooltips[2]}>
            <IconButton
              size="small"
              disabled={row.isLinking}
              onClick={() => handleLink(row.id)}
              sx={{ color: "#000", ":hover": { color: "#26890D" } }}
            >
              <AddCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
  },
];
};

export default function AISuggestionContent({
  status,
  data,
  rows: customRows,
  selectedRows = [],
  selectedComponents = [],
  selectedLegals = [],
  selectedTestCases = [],
  mode = "regular",
  loadingMessage = "AI suggestion loading...",
  loadingHistory = [],
  page = 1,
  rowsPerPage = 25,
  totalItems = 0,
  onPageChange = () => { },
  onWhyThis,
  onLinkRequest,
  onUnlinkRequest,
  onRowLink,
  onRowUnlink,
  onClose,
  closeDialogOnAdd = false,
  onOpenCombination = () => {},
  onOpenAlternate = () => {},
  onAddVc,
}) {
  const dispatch = useDispatch();

  const [rows, setRows] = useState([]);

  useEffect(() => {
    let initialRows = [];
    if (customRows) {
      initialRows = customRows;
    } else if (data?.recommendations?.length) {
      initialRows = mapRecommendationsToRows(data?.recommendations, data?.source);
    } else {
      initialRows = [];
    }

    const ticketContext = { selectedComponents, selectedLegals, selectedTestCases };

    initialRows = initialRows.map((r) => {
      const isLinked =
        mode === "preview"
          ? true
          : selectedRows.some((sel) => isRowMatch(sel, r)) ||
            isRowSelectedInTicket(r, ticketContext);
      return {
        ...r,
        isLinked,
      };
    });

    setRows(initialRows);
  }, [
    customRows,
    data,
    selectedRows,
    mode,
    selectedComponents,
    selectedLegals,
    selectedTestCases,
  ]);

  const handleLink = async (rowId) => {
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, isLinking: true } : r)),
    );
    const selectionPayload = adaptRowToSelectionPayload(row);

    if (onLinkRequest) {
      onLinkRequest(selectionPayload);
    }
    if (onRowLink) {
      onRowLink(row, selectionPayload);
    }
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, isLinked: true, isLinking: false } : r)),
    );
    if (closeDialogOnAdd && onClose) {
      onClose();
    }
  };

  const handleUnlink = async (rowId) => {
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, isLinking: true } : r)),
    );
    const eeItem = adaptRowToEEItem(row);
    if (onUnlinkRequest) {
      onUnlinkRequest(eeItem);
    }
    if (onRowUnlink) {
      onRowUnlink(row, eeItem);
    }

    setRows((prev) =>
      mode === "preview"
        ? prev.filter((r) => r.id !== rowId)
        : prev.map((r) =>
            r.id === rowId ? { ...r, isLinked: false, isLinking: false } : r,
          ),
    );
  };

  const submitFeedback = async (row, action) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === row.id ? { ...r, isProvidingFeedback: true } : r,
      ),
    );

    const payload = {
      sourceNodeId: row?.sourceNodeId,
      targetNodeId: row?.raw?.target?.entityId,
      action,
      context: {
        projectId: row?.raw?.projectId || "",
        variantId: row?.raw?.variantId || "",
        eeNodeId: row?.raw?.targetContext?.entityId || "",
      },
    };

    const resp = await dispatch(
      HandleApiActions({
        https_method: "POST",
        ...smartMatchActions.submit_feedback,
        params: payload,
        show_toast: false,
        ignore_app_loading_spinner: true,
      }),
    );

    setRows((prev) =>
      prev.map((r) =>
        r.id === row.id
          ? {
            ...r,
            feedback: action,
            isProvidingFeedback: false,
          }
          : r,
      ),
    );

    if (resp?.error) {
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id
            ? {
              ...r,
              feedback: null,
              isProvidingFeedback: false,
            }
            : r,
        ),
      );
    }
  };

  if (status === "loading") {
    const displayLoadingMessage = formatLoadingMessage(loadingMessage);

    return (
      <Box
        sx={{
          p: 3,
          textAlign: "center",
          height: "360px",
          width: "650px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography>{displayLoadingMessage}</Typography>
        <Box sx={{ width: "100%", maxWidth: "320px" }}>
          <LinearProgress
            sx={{
              height: "5px",
              borderRadius: "0px",
              backgroundColor: "#e0e0e0",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#26890D",
                borderRadius: "0px",
              },
            }}
          />
        </Box>
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box>
        <Box
          sx={{
            p: 3,
            textAlign: "center",
            color: mode === "preview" ? "text.secondary" : "error.main",
            height: "360px",
            width: "650px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography>
            {mode === "preview"
              ? "No selected components found."
              : "No suggestion found."}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "360px",
        width: "650px",
      }}
    >
      <CustomDataGridV2
        rows={rows}
        columns={getColumns({
          mode,
          onWhyThis,
          handleLink,
          handleUnlink,
          submitFeedback,
          onOpenCombination,
          onOpenAlternate,
          onAddVc,
          rows,
        })}
        hideHeader
        showPagination={mode !== "combination" && mode !== "preview"}
        isMultiLine={true}
        page={mode === "combination" || mode === "preview" ? 1 : page}
        rowsPerPage={
          mode === "combination" || mode === "preview"
            ? (rows.length || 25)
            : rowsPerPage
        }
        totalItems={
          mode === "combination" || mode === "preview"
            ? rows.length
            : totalItems
        }
        handlePageChange={onPageChange}
      />

    </Box>
  );
}

AISuggestionContent.propTypes = {
  status: PropTypes.string.isRequired,
  data: PropTypes.shape({
    recommendations: PropTypes.arrayOf(PropTypes.object),
    source: PropTypes.shape({
      nodeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  rows: PropTypes.arrayOf(PropTypes.object),
  mode: PropTypes.oneOf(["regular", "combination", "alternate", "preview"]),
  selectedRows: PropTypes.arrayOf(PropTypes.object),
  onRowLink: PropTypes.func,
  onRowUnlink: PropTypes.func,
  loadingMessage: PropTypes.string,
  loadingHistory: PropTypes.arrayOf(PropTypes.string),
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  totalItems: PropTypes.number,
  onPageChange: PropTypes.func,
  onWhyThis: PropTypes.func.isRequired,
  onLinkRequest: PropTypes.func.isRequired,
  onUnlinkRequest: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  closeDialogOnAdd: PropTypes.bool,
  onOpenCombination: PropTypes.func,
  onOpenAlternate: PropTypes.func,
  onAddVc: PropTypes.func,
};
